import { Injectable, signal } from '@angular/core';

const KEY = 'wiztasks_tab_lock';
const CHANNEL = 'wiztasks-tab-lock';
const HEARTBEAT_MS = 3000;
const STALE_MS = 9000; // a lock older than this is considered abandoned

interface LockRecord {
  id: string;
  ts: number;
}

/**
 * Single active tab per browser (security requirement): only one tab may drive a
 * signed-in WizTasks session at a time. The newest tab takes over; any other tab
 * shows a blocking overlay with a "Use here" button that re-claims control.
 *
 * Signalling: BroadcastChannel for an instant hand-off, plus the localStorage
 * `storage` event as a fallback, plus a heartbeat so a crashed tab's lock frees.
 */
@Injectable({ providedIn: 'root' })
export class TabLockService {
  /** True when THIS tab has been superseded by another tab. */
  readonly blocked = signal(false);

  private readonly tabId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  private channel: BroadcastChannel | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private started = false;

  /** Call once, after the app knows the user is signed in. */
  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    try {
      this.channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL) : null;
    } catch {
      this.channel = null;
    }
    this.channel?.addEventListener('message', (e: MessageEvent) => this.onSignal(e.data));
    window.addEventListener('storage', (e) => {
      if (e.key === KEY && e.newValue) {
        try {
          this.onSignal(JSON.parse(e.newValue));
        } catch {
          /* ignore */
        }
      }
    });
    window.addEventListener('beforeunload', () => this.release());

    // If a fresh lock is already held by another tab, defer to it briefly —
    // but still claim (newest tab wins) so a stale lock never wedges the app.
    this.claim();
    this.heartbeat = setInterval(() => {
      if (!this.blocked()) this.claim();
      else this.checkForStale();
    }, HEARTBEAT_MS);
  }

  /** Release the lock and stop participating (e.g. on sign-out). */
  stop(): void {
    if (!this.started) return;
    this.started = false;
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
    this.blocked.set(false);
    this.release();
  }

  /** Re-take control from this tab (the "Use here" button). */
  takeOver(): void {
    this.blocked.set(false);
    this.claim();
    if (typeof window !== 'undefined') window.location.reload();
  }

  private claim(): void {
    const rec: LockRecord = { id: this.tabId, ts: Date.now() };
    try {
      localStorage.setItem(KEY, JSON.stringify(rec));
    } catch {
      /* private mode / disabled storage — fall back to channel only */
    }
    this.channel?.postMessage(rec);
  }

  private release(): void {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const rec = JSON.parse(raw) as LockRecord;
        if (rec.id === this.tabId) localStorage.removeItem(KEY);
      }
    } catch {
      /* ignore */
    }
  }

  private onSignal(rec: unknown): void {
    if (!rec || typeof rec !== 'object') return;
    const r = rec as LockRecord;
    if (!r.id || r.id === this.tabId) return;
    // Another tab claimed control after us → this tab is now secondary.
    if (r.ts && Date.now() - r.ts < STALE_MS) {
      this.blocked.set(true);
      if (this.heartbeat) {
        // keep the interval running to detect the other tab closing
      }
    }
  }

  /** While blocked, reclaim automatically if the other tab's lock goes stale. */
  private checkForStale(): void {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) {
        this.blocked.set(false);
        this.claim();
        return;
      }
      const rec = JSON.parse(raw) as LockRecord;
      if (rec.id === this.tabId || Date.now() - (rec.ts || 0) > STALE_MS) {
        this.blocked.set(false);
        this.claim();
      }
    } catch {
      /* ignore */
    }
  }
}
